import {
  HORIZON_MAX,
  SURFACE_MAX,
  SURFACE_MIN,
  useActiveScenario,
  useScenarioStore,
} from "../../state/useScenarioStore";
import { pct } from "../../lib/format";

/** Sizes that cover most of what is actually on the market in the region. */
const SURFACE_PRESETS = [25, 45, 60, 80, 110];

/**
 * The three inputs a scenario is built from: size, horizon, growth.
 *
 * They edit the active scenario in place — there is no save step, because a
 * scenario you have to remember to save is one you will lose.
 */
export function ScenarioInputs({ historicRate }: { historicRate: number | null }) {
  const { surface, horizon, growthMode, customRate } = useActiveScenario();
  const setSurface = useScenarioStore((s) => s.setSurface);
  const setHorizon = useScenarioStore((s) => s.setHorizon);
  const setGrowthMode = useScenarioStore((s) => s.setGrowthMode);
  const setCustomRate = useScenarioStore((s) => s.setCustomRate);

  return (
    <section className="space-y-3.5">
      <div>
        <div className="flex items-baseline justify-between">
          <label
            htmlFor="scenario-surface"
            className="text-[10px] font-medium uppercase tracking-wider text-ink-soft"
          >
            Flat size
          </label>
          <span className="text-sm font-semibold tabular-nums">{surface} m²</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <input
            id="scenario-surface"
            type="range"
            min={SURFACE_MIN}
            max={150}
            step={1}
            value={Math.min(surface, 150)}
            onChange={(e) => setSurface(Number(e.target.value))}
            className="min-w-0 flex-1 accent-accent"
          />
          <input
            type="number"
            min={SURFACE_MIN}
            max={SURFACE_MAX}
            value={surface}
            onChange={(e) => setSurface(Number(e.target.value))}
            aria-label="Flat size in square metres"
            className="w-16 rounded border border-line bg-surface px-1.5 py-1 text-right text-[12px] tabular-nums outline-none focus:border-accent"
          />
        </div>
        <div className="mt-1.5 flex gap-1">
          {SURFACE_PRESETS.map((m2) => (
            <button
              key={m2}
              onClick={() => setSurface(m2)}
              className={`flex-1 rounded-md border px-1 py-0.5 text-[10px] tabular-nums transition-colors ${
                surface === m2
                  ? "border-accent text-accent"
                  : "border-line text-ink-soft hover:bg-surface-alt"
              }`}
            >
              {m2}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-baseline justify-between">
          <label
            htmlFor="scenario-horizon"
            className="text-[10px] font-medium uppercase tracking-wider text-ink-soft"
          >
            Horizon
          </label>
          <span className="text-sm font-semibold tabular-nums">
            {horizon} year{horizon === 1 ? "" : "s"}
          </span>
        </div>
        <input
          id="scenario-horizon"
          type="range"
          min={1}
          max={HORIZON_MAX}
          step={1}
          value={horizon}
          onChange={(e) => setHorizon(Number(e.target.value))}
          className="mt-1 w-full accent-accent"
        />
      </div>

      <div>
        <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-ink-soft">
          Growth assumption
        </div>
        <div className="flex rounded-lg border border-line p-0.5">
          {(
            [
              { value: "historic", label: "Area's own trend" },
              { value: "custom", label: "Fixed rate" },
            ] as const
          ).map((o) => (
            <button
              key={o.value}
              onClick={() => setGrowthMode(o.value)}
              className={`flex-1 rounded-md px-1 py-1 text-[11px] leading-tight transition-colors ${
                growthMode === o.value
                  ? "bg-accent font-medium text-white"
                  : "text-ink-soft hover:bg-surface-alt"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {growthMode === "custom" ? (
          <div className="mt-2">
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] font-medium uppercase tracking-wider text-ink-soft">
                Rate
              </span>
              <span className="text-sm font-semibold tabular-nums">
                {pct(customRate)}/yr
              </span>
            </div>
            <input
              type="range"
              min={-6}
              max={8}
              step={0.25}
              value={customRate * 100}
              onChange={(e) => setCustomRate(Number(e.target.value) / 100)}
              aria-label="Annual growth rate"
              className="mt-1 w-full accent-accent"
            />
            {historicRate !== null && (
              <button
                onClick={() => setCustomRate(Math.round(historicRate * 400) / 400)}
                className="mt-0.5 text-[10px] text-accent hover:underline"
              >
                Match this area ({pct(historicRate)}/yr)
              </button>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}
